// Quotes.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import "./Quotes.css";

/* -------------------------------------------------------------------------- */
/*                                  Constants                                 */
/* -------------------------------------------------------------------------- */

const STATUS_TABS = ["All", "Approved", "Rejected", "Expired"];

const emptyData = {
  kpis: { openQuoteAmount: 0, openQuoteCount: 0 },
  rows: [],
};

/* -------------------------------------------------------------------------- */
/*                                   Helpers                                  */
/* -------------------------------------------------------------------------- */

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(n);
};

const getStatusTone = (status) => {
  const s = String(status || "").toUpperCase();
  if (s === "SENT") return "sent";
  if (s === "VIEWED") return "viewed";
  if (s === "APPROVED") return "approved";
  if (s === "REJECTED") return "rejected";
  if (s === "EXPIRED") return "expired";
  return "neutral";
};

// Parse MM/DD/YYYY date string to a comparable timestamp (or null)
const parseMDY = (str) => {
  if (!str) return null;
  const parts = str.split("/");
  if (parts.length !== 3) return null;
  const d = new Date(
    Number(parts[2]),
    Number(parts[0]) - 1,
    Number(parts[1])
  );
  return Number.isNaN(d.getTime()) ? null : d;
};

// Parse YYYY-MM-DD (native date input value) to a comparable date
const parseYMD = (str) => {
  if (!str) return null;
  const d = new Date(str + "T00:00:00");
  return Number.isNaN(d.getTime()) ? null : d;
};

const matchesSearch = (row, term) => {
  if (!term) return true;
  const lower = term.toLowerCase();
  return [
    row.quoteNumber,
    row.title,
    row.description,
    row.status,
    row.poNumber,
    row.property,
    row.customer,
    row.approvalDate,
    row.sentDate,
    row.amount != null ? String(row.amount) : null,
  ]
    .filter(Boolean)
    .some((v) => String(v).toLowerCase().includes(lower));
};

const matchesDateRange = (row, fromDate, toDate) => {
  if (!fromDate && !toDate) return true;
  const sent = parseMDY(row.sentDate);
  if (!sent) return !fromDate && !toDate;
  if (fromDate && sent < fromDate) return false;
  if (toDate && sent > toDate) return false;
  return true;
};

/* -------------------------------------------------------------------------- */
/*                              Reusable UI pieces                            */
/* -------------------------------------------------------------------------- */

const KpiCard = ({ label, value, isLoading }) => (
  <div className="quotes-kpi-card">
    <div className="quotes-kpi-card__label">{label}</div>
    <div className="quotes-kpi-card__value">
      {isLoading ? <span className="card-spinner" /> : value}
    </div>
  </div>
);

const SkeletonRows = ({ cols, count = 6 }) =>
  Array.from({ length: count }).map((_, i) => (
    <tr key={i}>
      {Array.from({ length: cols }).map((_, j) => (
        <td key={j}><span className="skeleton-line" /></td>
      ))}
    </tr>
  ));

const CompanySelect = ({ customers, selectedCustomerId, setSelectedCustomerId, disabled }) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  useEffect(() => {
    const onClickOutside = (e) => {
      if (!wrapperRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);
  const selected = customers.find((c) => String(c.id) === String(selectedCustomerId));
  const buttonLabel = selected ? selected.name : "All Companies";
  return (
    <div ref={wrapperRef} className="quotes-filter-field">
      <label className="quotes-filter-field__label">Company Selection</label>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((p) => !p)}
        className="quotes-select-btn"
      >
        <span className="quotes-select-btn__text">{buttonLabel}</span>
        <span className={`quotes-select-btn__chevron ${open ? "is-open" : ""}`}>▾</span>
      </button>
      {open && !disabled ? (
        <div className="quotes-multiselect-menu">
          <div className="quotes-multiselect-list">
            <div
              className={`quotes-checkbox-row${!selectedCustomerId ? " is-selected" : ""}`}
              onClick={() => { setSelectedCustomerId(""); setOpen(false); }}
            >
              <span>All Companies</span>
            </div>
            {customers.map((c) => (
              <div
                key={c.id}
                className={`quotes-checkbox-row${String(selectedCustomerId) === String(c.id) ? " is-selected" : ""}`}
                onClick={() => { setSelectedCustomerId(String(c.id)); setOpen(false); }}
              >
                <span>{c.name}</span>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
};

const PropertyMultiSelect = ({
  properties,
  selectedPropertyIds,
  setSelectedPropertyIds,
  disabled,
}) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const onOutside = (e) => {
      if (!wrapperRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onOutside);
    return () => document.removeEventListener("mousedown", onOutside);
  }, []);

  const selectedNames = properties
    .filter((p) => selectedPropertyIds.includes(String(p.id)))
    .map((p) => p.name);

  const buttonLabel = (() => {
    if (!selectedPropertyIds.length) return "Select values";
    if (selectedPropertyIds.length === 1) return selectedNames[0] || "1 Property";
    return `${selectedPropertyIds.length} selected`;
  })();

  const toggle = (id) => {
    setSelectedPropertyIds((curr) => {
      const s = String(id);
      return curr.includes(s) ? curr.filter((x) => x !== s) : [...curr, s];
    });
  };

  return (
    <div ref={wrapperRef} className="quotes-filter-field">
      <label className="quotes-filter-field__label">Property Selection</label>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((p) => !p)}
        className={`quotes-select-btn ${open ? "is-open" : ""}`}
      >
        <span className="quotes-select-btn__text">{buttonLabel}</span>
        <span className={`quotes-select-btn__chevron ${open ? "is-open" : ""}`}>
          ▾
        </span>
      </button>

      {open && !disabled ? (
        <div className="quotes-multiselect-menu">
          <div className="quotes-multiselect-actions">
            <button
              type="button"
              className="quotes-pill-btn"
              onClick={() => setSelectedPropertyIds([])}
            >
              All
            </button>
            <button
              type="button"
              className="quotes-pill-btn"
              onClick={() =>
                setSelectedPropertyIds(
                  properties.map((p) => String(p.id))
                )
              }
            >
              Select All
            </button>
          </div>
          <div className="quotes-multiselect-list">
            {properties.length ? (
              properties.map((p) => (
                <label key={p.id} className="quotes-checkbox-row">
                  <input
                    type="checkbox"
                    checked={selectedPropertyIds.includes(String(p.id))}
                    onChange={() => toggle(p.id)}
                  />
                  <span>{p.name}</span>
                </label>
              ))
            ) : (
              <div className="quotes-multiselect-empty">
                No properties available.
              </div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};

const DateRangeFilter = ({ fromDate, toDate, setFromDate, setToDate }) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const onOutside = (e) => {
      if (!wrapperRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onOutside);
    return () => document.removeEventListener("mousedown", onOutside);
  }, []);

  const hasValue = fromDate || toDate;
  const label = (() => {
    if (fromDate && toDate) return `${fromDate} – ${toDate}`;
    if (fromDate) return `From ${fromDate}`;
    if (toDate) return `To ${toDate}`;
    return "Select date range";
  })();

  return (
    <div ref={wrapperRef} className="quotes-filter-field">
      <label className="quotes-filter-field__label">Date Sent Filter</label>
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        className={`quotes-select-btn ${hasValue ? "has-value" : ""}`}
      >
        <span className="quotes-date-icon">&#128197;</span>
        <span className="quotes-select-btn__text">{label}</span>
        <span className={`quotes-select-btn__chevron ${open ? "is-open" : ""}`}>
          ▾
        </span>
      </button>

      {open ? (
        <div className="quotes-date-menu">
          <div className="quotes-date-row">
            <label className="quotes-date-label">From</label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="quotes-date-input"
            />
          </div>
          <div className="quotes-date-row">
            <label className="quotes-date-label">To</label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="quotes-date-input"
            />
          </div>
          {hasValue ? (
            <button
              type="button"
              className="quotes-pill-btn"
              onClick={() => {
                setFromDate("");
                setToDate("");
              }}
            >
              Clear
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/*                                   Quotes                                   */
/* -------------------------------------------------------------------------- */

const Quotes = ({ viewerEmail }) => {
  const [filtersLoading, setFiltersLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(false);

  const [customers, setCustomers] = useState([]);
  const [properties, setProperties] = useState([]);

  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [selectedPropertyIds, setSelectedPropertyIds] = useState([]);

  const [filterError, setFilterError] = useState("");
  const [dataError, setDataError] = useState("");

  const [data, setData] = useState(emptyData);

  const [activeTab, setActiveTab] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // Load filters
  useEffect(() => {
    const load = async () => {
      try {
        setFiltersLoading(true);
        setFilterError("");

        const token = localStorage.getItem("token");
        if (!token) {
          setFilterError("No auth token found.");
          return;
        }

        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/quotes/quotes-filters`,
          {
            headers: { Authorization: `Bearer ${token}` },
            params: viewerEmail ? { email: viewerEmail } : undefined,
          }
        );

        const d = res?.data || {};
        setCustomers(Array.isArray(d.customers) ? d.customers : []);
        setProperties(Array.isArray(d.properties) ? d.properties : []);
      } catch (err) {
        console.error("Failed to load quotes filters:", err);
        setFilterError("Failed to load filters.");
      } finally {
        setFiltersLoading(false);
      }
    };

    load();
  }, [viewerEmail]);

  const filteredProperties = useMemo(() => {
    if (!selectedCustomerId) return properties;
    return properties.filter(
      (p) => String(p.customerId ?? "") === String(selectedCustomerId)
    );
  }, [properties, selectedCustomerId]);

  // Drop selected property IDs when switching customer
  useEffect(() => {
    const allowed = new Set(filteredProperties.map((p) => String(p.id)));
    setSelectedPropertyIds((curr) => curr.filter((id) => allowed.has(id)));
  }, [filteredProperties]);

  // Load data when filters change
  useEffect(() => {
    const load = async () => {
      if (filtersLoading || filterError) return;

      try {
        setDataLoading(true);
        setDataError("");

        const token = localStorage.getItem("token");
        if (!token) {
          setDataError("No auth token found.");
          return;
        }

        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/quotes/quotes-data`,
          {
            headers: { Authorization: `Bearer ${token}` },
            params: {
              ...(viewerEmail ? { email: viewerEmail } : {}),
              ...(selectedCustomerId ? { customerId: selectedCustomerId } : {}),
              ...(selectedPropertyIds.length
                ? { propertyIds: selectedPropertyIds.join(",") }
                : {}),
            },
          }
        );

        setData(res?.data || emptyData);
      } catch (err) {
        console.error("Failed to load quotes data:", err);
        setDataError("Failed to load quotes data.");
        setData(emptyData);
      } finally {
        setDataLoading(false);
      }
    };

    load();
  }, [
    filtersLoading,
    filterError,
    viewerEmail,
    selectedCustomerId,
    selectedPropertyIds,
  ]);

  const fromDateObj = useMemo(() => parseYMD(fromDate), [fromDate]);
  const toDateObj = useMemo(() => parseYMD(toDate), [toDate]);

  // Compute visible rows: tab filter → date filter → search
  const visibleRows = useMemo(() => {
    let rows = data.rows || [];

    if (activeTab !== "All") {
      rows = rows.filter(
        (r) =>
          String(r.status || "").toUpperCase() === activeTab.toUpperCase()
      );
    }

    rows = rows.filter((r) => matchesDateRange(r, fromDateObj, toDateObj));
    rows = rows.filter((r) => matchesSearch(r, searchTerm));

    return rows;
  }, [data.rows, activeTab, fromDateObj, toDateObj, searchTerm]);

  const handleClearSearch = () => {
    setSelectedCustomerId("");
    setSelectedPropertyIds([]);
    setActiveTab("All");
    setSearchTerm("");
    setFromDate("");
    setToDate("");
  };

  const isLoading = filtersLoading || dataLoading;

  return (
    <div className="quotes-page">
      <div className="quotes-shell">
        {/* ---- Filter bar ---- */}
        <section className="quotes-toolbar">
          <div className="quotes-toolbar__grid">
            {/* Company */}
            <CompanySelect
              customers={customers}
              selectedCustomerId={selectedCustomerId}
              setSelectedCustomerId={setSelectedCustomerId}
              disabled={filtersLoading || !!filterError}
            />

            {/* Property */}
            <PropertyMultiSelect
              properties={filteredProperties}
              selectedPropertyIds={selectedPropertyIds}
              setSelectedPropertyIds={setSelectedPropertyIds}
              disabled={filtersLoading || !!filterError}
            />

            {/* Date sent */}
            <DateRangeFilter
              fromDate={fromDate}
              toDate={toDate}
              setFromDate={setFromDate}
              setToDate={setToDate}
            />

            {/* Search */}
            <div className="quotes-filter-field quotes-filter-field--search">
              <label className="quotes-filter-field__label">Search</label>
              <input
                type="text"
                placeholder=""
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="quotes-search-input"
              />
            </div>

            {/* Clear */}
            <div className="quotes-toolbar__clear">
              <button
                type="button"
                onClick={handleClearSearch}
                className="quotes-clear-btn"
              >
                Clear Search
              </button>
            </div>
          </div>
        </section>

        {/* ---- Feedback ---- */}

        {filterError ? (
          <div className="quotes-feedback quotes-feedback--error">
            {filterError}
          </div>
        ) : null}

        {dataError ? (
          <div className="quotes-feedback quotes-feedback--error">
            {dataError}
          </div>
        ) : null}

        {!filterError ? (
          <>
            {/* ---- KPI cards ---- */}
            <section className="quotes-kpi-grid">
              <KpiCard
                label="Open Quote Amount"
                value={formatCurrency(data.kpis?.openQuoteAmount ?? 0)}
                isLoading={isLoading}
              />
              <KpiCard
                label="Open Quotes"
                value={data.kpis?.openQuoteCount ?? 0}
                isLoading={isLoading}
              />
            </section>

            {/* ---- Status tabs + table ---- */}
            <section className="quotes-table-card">
              <div className="quotes-table-card__header">
                <span className="quotes-table-card__title">Quote Status</span>
              </div>

              <div className="quotes-tabs">
                {STATUS_TABS.map((tab) => (
                  <button
                    key={tab}
                    type="button"
                    onClick={() => setActiveTab(tab)}
                    className={`quotes-tab ${activeTab === tab ? "is-active" : ""}`}
                  >
                    {tab}
                  </button>
                ))}
              </div>

              <div className="quotes-table-wrap">
                {isLoading || visibleRows.length > 0 ? (
                  <table className="quotes-table">
                    <thead>
                      <tr>
                        <th>Quote</th>
                        <th>Title</th>
                        <th>Description</th>
                        <th>Status</th>
                        <th>PO Number</th>
                        <th>Quote Amount</th>
                        <th>Approval Date</th>
                        <th>Sent Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {isLoading ? <SkeletonRows cols={8} /> : visibleRows.map((row, idx) => (
                        <tr
                          key={row.quoteId || `row-${idx}`}
                          className={idx % 2 === 0 ? "" : "is-alt"}
                        >
                          <td>
                            <div className="quotes-primary-cell">
                              {row.quoteNumber || "—"}
                            </div>
                          </td>
                          <td>{row.title || "—"}</td>
                          <td className="quotes-description-cell">
                            {row.description || "—"}
                          </td>
                          <td>
                            <span
                              className={`quotes-status-pill quotes-status-pill--${getStatusTone(
                                row.status
                              )}`}
                            >
                              {row.status || "—"}
                            </span>
                          </td>
                          <td>{row.poNumber || "—"}</td>
                          <td>{formatCurrency(row.amount)}</td>
                          <td>{row.approvalDate || "—"}</td>
                          <td>{row.sentDate || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="quotes-empty-state">No quotes found</div>
                )}
              </div>
            </section>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default Quotes;
